import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { STATUS_LABELS, STATUS_COLORS, PETITION_TYPES } from "@/lib/constants";
import { formatDate } from "@/lib/constants";
import { ArrowRight, AlertTriangle } from "lucide-react";

const daysSince = (d) => Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));

const flagsOf = (p, limit) => ({ urgent: p.priority === "acil", late: !!p.date && daysSince(p.date) > limit });

const PetitionRow = ({ p, villages, limit }) => {
  const { urgent, late } = flagsOf(p, limit);
  const hot = urgent || late;
  return (
    <div className={`py-3 px-3 -mx-3 flex flex-wrap items-center gap-x-4 gap-y-1 ${hot ? "bg-rose-50 border-l-4 border-rose-600" : ""}`}
         data-testid={`pending-petition-${p.id}`} data-flagged={hot ? "true" : "false"}>
      <div className={`font-mono text-xs w-24 ${hot ? "text-rose-700" : "text-slate-500"}`}>{formatDate(p.date)}</div>
      <div className="flex-1 min-w-[200px]">
        <div className={`text-sm font-medium truncate ${hot ? "text-rose-800" : "text-slate-900"}`}>{p.subject}</div>
        <div className="text-xs text-slate-500">
          {p.applicant_name} · {villages[p.village_id] || "Köy belirtilmemiş"} · {PETITION_TYPES[p.petition_type]}
        </div>
      </div>
      {urgent && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-600 text-white" data-testid={`pending-urgent-${p.id}`}>Acil</span>}
      {late && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border border-rose-300 text-rose-700 bg-white" data-testid={`pending-late-${p.id}`}>Gecikmiş</span>}
      <div className={`text-xs whitespace-nowrap ${late ? "text-rose-700 font-semibold" : "text-slate-500"}`}>{daysSince(p.date)} gündür</div>
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[p.status]}`}>{STATUS_LABELS[p.status]}</span>
    </div>
  );
};

export const PendingPetitionsWidget = ({ title, range = {} }) => {
  const [items, setItems] = useState(null);
  const [villages, setVillages] = useState({});
  const [limit, setLimit] = useState(30);

  useEffect(() => {
    Promise.all([
      api.get("/petitions", { params: { status: "beklemede" } }),
      api.get("/petitions", { params: { status: "islemde" } }),
    ]).then(([a, b]) => setItems([...a.data, ...b.data].sort((x, y) => (x.date || "").localeCompare(y.date || ""))));
    api.get("/villages").then((r) => setVillages(Object.fromEntries(r.data.map((v) => [v.id, v.name]))));
    api.get("/settings/petition-reminder").then((r) => setLimit(r.data.days || 30)).catch(() => {});
  }, []);

  const inRange = (p) => (!range.date_from || (p.date || "") >= range.date_from) && (!range.date_to || ((p.date || "").slice(0, 10) <= range.date_to && !!p.date));
  const isHot = (p) => { const f = flagsOf(p, limit); return f.urgent || f.late; };
  const shown = items ? [...items.filter(inRange)].sort((a, b) => isHot(b) - isHot(a)) : null;
  const hotCount = shown ? shown.filter(isHot).length : 0;
  return (
    <Card className="p-6 border border-slate-200 shadow-sm mt-6" data-testid="pending-petitions-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          {title}
          <p className="text-xs text-slate-500 mt-1">Beklemede ve işlemde olan dilekçeler — acil ve {limit} günden uzun bekleyenler kırmızıyla en üstte</p>
        </div>
        <Link to="/dilekceler" className="text-sm text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
              data-testid="pending-petitions-all-link">
          Tüm dilekçeler <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {hotCount > 0 && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-rose-600 text-white text-sm font-medium" data-testid="pending-petitions-alert">
          <AlertTriangle className="h-4 w-4" /> {hotCount} dilekçe acil ya da {limit} günden uzun süredir bekliyor
        </div>
      )}
      {shown && shown.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center" data-testid="pending-petitions-empty">
          {range.date_from || range.date_to ? "Seçilen dönemde sonuçlanmayı bekleyen dilekçe yok." : "Sonuçlanmayı bekleyen dilekçe yok."}
        </div>
      )}
      {shown && shown.length > 0 && (
        <div className="divide-y divide-slate-100" data-testid="pending-petitions-list">
          {shown.map((p) => <PetitionRow key={p.id} p={p} villages={villages} limit={limit} />)}
        </div>
      )}
    </Card>
  );
};
